import message from "@/utils/globalMessage";
import React, { useState } from 'react';
import { Card, Button, Typography, Form, Input, Row, Col } from "antd";
import { api } from '@/redux/api';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useKeepAliveContext } from '@/hooks/use-keep-alive-context';
import { UserCompanyFormItem } from '../components/UserCompanyFormItem';
import { UserRoleFormItem } from '../components/UserRoleFormItem';
import { PermissionProfileFormItem } from '../components/PermissionProfileFormItem';
import { AssigningFacilityFormItem } from '../components/AssigningFacilityFormItem';

const { Title } = Typography;

export default function InviteUser() {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const creatorCompany = useSelector(state => state.Facility?.selected?.record?.company_id);
    const creatorCompanyName = useSelector(state => state.Facility?.selected?.record?.company);
    const { closeTab } = useKeepAliveContext();
    const [loading, setLoading] = useState(false);

    const inviteUser = () => {
        form.validateFields().then((values) => {
            const role = values.role || 4;
            const needAssigningFacilityRequired = !(role === 1 || role === 3);
            
            // 如果角色需要分配设施，验证 user 字段
            if (needAssigningFacilityRequired && (!values.user || values.user.length === 0)) {
                message.error('Please select at least one facility!');
                return;
            }

            const resultData = {
                email: values.email,
                company: values.company?.value || values.company,
                role: role,
                profile: values.profile?.value || values.profile || null,
                user: values.user
            };

            // 如果角色需要分配设施，添加 user 字段（facility id 数组）
            // if (needAssigningFacilityRequired && values.user && values.user.length > 0) {
            //     resultData.user = values.user;
            // }

            setLoading(true);
            api.inviteUser(resultData).then(response => {
                if (response.status >= 200 && response.status < 300) {
                    if (response.data.warning) {
                        message.warning(`User created but email failed: ${response.data.warning}`);
                    } else {
                        message.success('Invitation email sent successfully');
                    }
                    closeTab();
                    navigate('/admin/user-management');
                } else {
                    const errorData = response.data || {};
                    const errorMsg = Object.values(errorData).flat().join(', ') || 'Invitation failed';
                    message.error(errorMsg);
                }
            }).catch(error => {
                message.error('Invitation failed');
                console.error(error);
            }).finally(() => {
                setLoading(false);
            });
        }).catch(error => {
            console.error('Validation failed:', error);
            message.error('Please check the form fields');
        });
    }

    return (
        <Card
            title={<Title level={4}>Invite User</Title>}
            extra={
                <Button loading={loading} type="primary" onClick={inviteUser}>Invite</Button>
            }
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    role: 4,
                    company: {
                        label: creatorCompanyName,
                        value: creatorCompany,
                    }
                }}
            >
                <Row gutter={24}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Email"
                            name="email"
                            required
                            rules={[
                                { required: true, message: 'Please input email!' },
                                {
                                    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/,
                                    message: 'Please input a valid email address!'
                                }
                            ]}
                        >
                            <Input placeholder="Enter email address" />
                        </Form.Item>
                    </Col>
                    
                    <Col xs={24} md={12}>
                        <UserCompanyFormItem />
                    </Col>

                    <Col xs={24}>
                        <Form.Item
                            label="Role"
                            name="role"
                            rules={[{ required: true, message: 'Please select role!' }]}
                        >
                            <UserRoleFormItem />
                        </Form.Item>
                    </Col>
                    
                    <Col xs={12}>
                        <PermissionProfileFormItem />
                    </Col>

                    <Col xs={24}>
                        <Form.Item
                            label="Assigning Company and Facility"
                            name="user"
                            dependencies={['role', 'company']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const role = getFieldValue('role');
                                        const needAssigningFacilityRequired = !(role === 1 || role === 3);
                                        
                                        if (!needAssigningFacilityRequired) {
                                            return Promise.resolve();
                                        }
                                        
                                        if (!value || value.length === 0) {
                                            return Promise.reject(new Error('Please select at least one facility!'));
                                        }
                                        
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <AssigningFacilityFormItem disabled={false} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}


